/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { Document } from '../models/document';
import { DocumentClassification } from '../models/document-classification';
import { DocumentClassificationResult } from '../models/document-classification-result';
import { DocumentType } from '../models/document-type';
import { Documents } from '../models/documents';
import { JsonPatchDocument } from '../models/json-patch-document';
import { RegisterDocument } from '../models/register-document';
import { RequiredDocumentCondition } from '../models/required-document-condition';
import { RequiredDocumentRule } from '../models/required-document-rule';
import { UploadedDocumentResult } from '../models/uploaded-document-result';

@Injectable({
  providedIn: 'root',
})
export class DocumentsService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation getDocument
   */
  static readonly GetDocumentPath = '/api/v1/Documents/{id}';

  /**
   * Returns a single document.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getDocument$Plain()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDocument$Plain$Response(params: {

    /**
     * The id of the document to be fetched
     */
    id: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Document>> {

    const rb = new RequestBuilder(this.rootUrl, DocumentsService.GetDocumentPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Document>;
      })
    );
  }

  /**
   * Returns a single document.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getDocument$Plain$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDocument$Plain(params: {

    /**
     * The id of the document to be fetched
     */
    id: string;
  },
  context?: HttpContext

): Observable<Document> {

    return this.getDocument$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<Document>) => r.body as Document)
    );
  }

  /**
   * Returns a single document.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getDocument()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDocument$Response(params: {

    /**
     * The id of the document to be fetched
     */
    id: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Document>> {

    const rb = new RequestBuilder(this.rootUrl, DocumentsService.GetDocumentPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Document>;
      })
    );
  }

  /**
   * Returns a single document.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getDocument$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDocument(params: {

    /**
     * The id of the document to be fetched
     */
    id: string;
  },
  context?: HttpContext

): Observable<Document> {

    return this.getDocument$Response(params,context).pipe(
      map((r: StrictHttpResponse<Document>) => r.body as Document)
    );
  }

  /**
   * Path part for operation classifyDocument
   */
  static readonly ClassifyDocumentPath = '/api/v1/Documents/{id}/classify';

  /**
   * Detects the document type and dates (issue date, expiration date, year) of a registered document from its content.
   * Call this after POST /documents to index the document automatically.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `classifyDocument()` instead.
   *
   * This method doesn't expect any request body.
   */
  classifyDocument$Response(params: {

    /**
     * The minimum confidence (0-1) required to store the detected values
     */
    minimumConfidence?: number;

    /**
     * The id of the document to classify
     */
    id: string;

    /**
     * Store the detected values on the document. Values that are already filled in are never overwritten.
     * Pass false to only get a suggestion.
     */
    apply?: boolean;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<DocumentClassificationResult>> {

    const rb = new RequestBuilder(this.rootUrl, DocumentsService.ClassifyDocumentPath, 'post');
    if (params) {
      rb.query('minimumConfidence', params.minimumConfidence, {});
      rb.path('id', params.id, {});
      rb.query('apply', params.apply, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<DocumentClassificationResult>;
      })
    );
  }

  /**
   * Detects the document type and dates (issue date, expiration date, year) of a registered document from its content.
   * Call this after POST /documents to index the document automatically.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `classifyDocument$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  classifyDocument(params: {

    /**
     * The minimum confidence (0-1) required to store the detected values
     */
    minimumConfidence?: number;

    /**
     * The id of the document to classify
     */
    id: string;

    /**
     * Store the detected values on the document. Values that are already filled in are never overwritten.
     * Pass false to only get a suggestion.
     */
    apply?: boolean;
  },
  context?: HttpContext

): Observable<DocumentClassificationResult> {

    return this.classifyDocument$Response(params,context).pipe(
      map((r: StrictHttpResponse<DocumentClassificationResult>) => r.body as DocumentClassificationResult)
    );
  }

  /**
   * Path part for operation validateDocument
   */
  static readonly ValidateDocumentPath = '/api/v1/Documents/{id}/validate';

  /**
   * Marks the document type and dates as checked by the current user: either after accepting the automatic
   * classification, or after categorizing the document manually.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `validateDocument()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  validateDocument$Response(params: {

    /**
     * The id of the document to validate
     */
    id: string;

    /**
     * Optional: a suggested classification (from /classify) the user accepted. It is stored
     * first, without overwriting values that are already filled in.
     */
    body?: DocumentClassification
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Document>> {

    const rb = new RequestBuilder(this.rootUrl, DocumentsService.ValidateDocumentPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Document>;
      })
    );
  }

  /**
   * Marks the document type and dates as checked by the current user: either after accepting the automatic
   * classification, or after categorizing the document manually.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `validateDocument$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  validateDocument(params: {

    /**
     * The id of the document to validate
     */
    id: string;

    /**
     * Optional: a suggested classification (from /classify) the user accepted. It is stored
     * first, without overwriting values that are already filled in.
     */
    body?: DocumentClassification
  },
  context?: HttpContext

): Observable<Document> {

    return this.validateDocument$Response(params,context).pipe(
      map((r: StrictHttpResponse<Document>) => r.body as Document)
    );
  }

  /**
   * Path part for operation patchDocument
   */
  static readonly PatchDocumentPath = '/api/v1/Documents/{id}';

  /**
   * Patch a document to change basic properties.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `patchDocument$Plain()` instead.
   *
   * This method sends `application/json-patch+json` and handles request body of type `application/json-patch+json`.
   */
  patchDocument$Plain$Response(params: {

    /**
     * The id of the document to patch
     */
    id: string;

    /**
     * Details of the operation in JSON patch format
     */
    body?: JsonPatchDocument
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Document>> {

    const rb = new RequestBuilder(this.rootUrl, DocumentsService.PatchDocumentPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json-patch+json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Document>;
      })
    );
  }

  /**
   * Patch a document to change basic properties.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `patchDocument$Plain$Response()` instead.
   *
   * This method sends `application/json-patch+json` and handles request body of type `application/json-patch+json`.
   */
  patchDocument$Plain(params: {

    /**
     * The id of the document to patch
     */
    id: string;

    /**
     * Details of the operation in JSON patch format
     */
    body?: JsonPatchDocument
  },
  context?: HttpContext

): Observable<Document> {

    return this.patchDocument$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<Document>) => r.body as Document)
    );
  }

  /**
   * Patch a document to change basic properties.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `patchDocument()` instead.
   *
   * This method sends `application/json-patch+json` and handles request body of type `application/json-patch+json`.
   */
  patchDocument$Response(params: {

    /**
     * The id of the document to patch
     */
    id: string;

    /**
     * Details of the operation in JSON patch format
     */
    body?: JsonPatchDocument
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Document>> {

    const rb = new RequestBuilder(this.rootUrl, DocumentsService.PatchDocumentPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json-patch+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Document>;
      })
    );
  }

  /**
   * Patch a document to change basic properties.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `patchDocument$Response()` instead.
   *
   * This method sends `application/json-patch+json` and handles request body of type `application/json-patch+json`.
   */
  patchDocument(params: {

    /**
     * The id of the document to patch
     */
    id: string;

    /**
     * Details of the operation in JSON patch format
     */
    body?: JsonPatchDocument
  },
  context?: HttpContext

): Observable<Document> {

    return this.patchDocument$Response(params,context).pipe(
      map((r: StrictHttpResponse<Document>) => r.body as Document)
    );
  }

  /**
   * Path part for operation getDocuments
   */
  static readonly GetDocumentsPath = '/api/v1/Documents';

  /**
   * Gets the documents for the current organization.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getDocuments$Plain()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDocuments$Plain$Response(params?: {

    /**
     * Mandatory: the customerId for which to retrieve the documents
     */
    customerId?: string;

    /**
     * The number of documents to be retrieved
     */
    count?: number;

    /**
     * The page that's used to offset the documents based on the count
     */
    page?: number;

    /**
     * Only retrieve documents of type that associated with a building
     */
    associatedWithBuilding?: boolean;

    /**
     * Only retrieve documents of type that associated with a mortgage
     */
    associatedWithMortgage?: boolean;

    /**
     * Only retrieve documents of type that associated with an application
     */
    associatedWithApplication?: boolean;

    /**
     * Only retrieve documents associated with a customer
     */
    associatedWithCustomer?: boolean;

    /**
     * Only retrieve documents of a particular documentType
     */
    documentTypeId?: string;

    /**
     * Only retrieve documents that have a document type or not (leave empty to get all)
     */
    hasDocumentType?: boolean;

    /**
     * Only retrieve documents uploaded in this month
     */
    uploadMonth?: number;

    /**
     * Only retrieve documents uploaded in this year
     */
    uploadYear?: number;

    /**
     * Only retrieve documents associated with this building
     */
    buildingId?: string;

    /**
     * Only retrieve documents associated with this application
     */
    applicationId?: string;

    /**
     * Only retrieve documents associated with this mortgage
     */
    mortgageId?: string;

    /**
     * Only retrieve documents associated with this task
     */
    taskId?: string;

    /**
     * Only retrieve documents that are user uploaded or non-user uploaded
     */
    isUserUploaded?: boolean;

    /**
     * Only retrieve documents that were classified automatically but not yet validated by a user
     */
    needsValidation?: boolean;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Documents>> {

    const rb = new RequestBuilder(this.rootUrl, DocumentsService.GetDocumentsPath, 'get');
    if (params) {
      rb.query('customerId', params.customerId, {});
      rb.query('count', params.count, {});
      rb.query('page', params.page, {});
      rb.query('associatedWithBuilding', params.associatedWithBuilding, {});
      rb.query('associatedWithMortgage', params.associatedWithMortgage, {});
      rb.query('associatedWithApplication', params.associatedWithApplication, {});
      rb.query('associatedWithCustomer', params.associatedWithCustomer, {});
      rb.query('documentTypeId', params.documentTypeId, {});
      rb.query('hasDocumentType', params.hasDocumentType, {});
      rb.query('uploadMonth', params.uploadMonth, {});
      rb.query('uploadYear', params.uploadYear, {});
      rb.query('buildingId', params.buildingId, {});
      rb.query('applicationId', params.applicationId, {});
      rb.query('mortgageId', params.mortgageId, {});
      rb.query('taskId', params.taskId, {});
      rb.query('isUserUploaded', params.isUserUploaded, {});
      rb.query('needsValidation', params.needsValidation, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Documents>;
      })
    );
  }

  /**
   * Gets the documents for the current organization.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getDocuments$Plain$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDocuments$Plain(params?: {

    /**
     * Mandatory: the customerId for which to retrieve the documents
     */
    customerId?: string;

    /**
     * The number of documents to be retrieved
     */
    count?: number;

    /**
     * The page that's used to offset the documents based on the count
     */
    page?: number;

    /**
     * Only retrieve documents of type that associated with a building
     */
    associatedWithBuilding?: boolean;

    /**
     * Only retrieve documents of type that associated with a mortgage
     */
    associatedWithMortgage?: boolean;

    /**
     * Only retrieve documents of type that associated with an application
     */
    associatedWithApplication?: boolean;

    /**
     * Only retrieve documents associated with a customer
     */
    associatedWithCustomer?: boolean;

    /**
     * Only retrieve documents of a particular documentType
     */
    documentTypeId?: string;

    /**
     * Only retrieve documents that have a document type or not (leave empty to get all)
     */
    hasDocumentType?: boolean;

    /**
     * Only retrieve documents uploaded in this month
     */
    uploadMonth?: number;

    /**
     * Only retrieve documents uploaded in this year
     */
    uploadYear?: number;

    /**
     * Only retrieve documents associated with this building
     */
    buildingId?: string;

    /**
     * Only retrieve documents associated with this application
     */
    applicationId?: string;

    /**
     * Only retrieve documents associated with this mortgage
     */
    mortgageId?: string;

    /**
     * Only retrieve documents associated with this task
     */
    taskId?: string;

    /**
     * Only retrieve documents that are user uploaded or non-user uploaded
     */
    isUserUploaded?: boolean;

    /**
     * Only retrieve documents that were classified automatically but not yet validated by a user
     */
    needsValidation?: boolean;
  },
  context?: HttpContext

): Observable<Documents> {

    return this.getDocuments$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<Documents>) => r.body as Documents)
    );
  }

  /**
   * Gets the documents for the current organization.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getDocuments()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDocuments$Response(params?: {

    /**
     * Mandatory: the customerId for which to retrieve the documents
     */
    customerId?: string;

    /**
     * The number of documents to be retrieved
     */
    count?: number;

    /**
     * The page that's used to offset the documents based on the count
     */
    page?: number;

    /**
     * Only retrieve documents of type that associated with a building
     */
    associatedWithBuilding?: boolean;

    /**
     * Only retrieve documents of type that associated with a mortgage
     */
    associatedWithMortgage?: boolean;

    /**
     * Only retrieve documents of type that associated with an application
     */
    associatedWithApplication?: boolean;

    /**
     * Only retrieve documents associated with a customer
     */
    associatedWithCustomer?: boolean;

    /**
     * Only retrieve documents of a particular documentType
     */
    documentTypeId?: string;

    /**
     * Only retrieve documents that have a document type or not (leave empty to get all)
     */
    hasDocumentType?: boolean;

    /**
     * Only retrieve documents uploaded in this month
     */
    uploadMonth?: number;

    /**
     * Only retrieve documents uploaded in this year
     */
    uploadYear?: number;

    /**
     * Only retrieve documents associated with this building
     */
    buildingId?: string;

    /**
     * Only retrieve documents associated with this application
     */
    applicationId?: string;

    /**
     * Only retrieve documents associated with this mortgage
     */
    mortgageId?: string;

    /**
     * Only retrieve documents associated with this task
     */
    taskId?: string;

    /**
     * Only retrieve documents that are user uploaded or non-user uploaded
     */
    isUserUploaded?: boolean;

    /**
     * Only retrieve documents that were classified automatically but not yet validated by a user
     */
    needsValidation?: boolean;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Documents>> {

    const rb = new RequestBuilder(this.rootUrl, DocumentsService.GetDocumentsPath, 'get');
    if (params) {
      rb.query('customerId', params.customerId, {});
      rb.query('count', params.count, {});
      rb.query('page', params.page, {});
      rb.query('associatedWithBuilding', params.associatedWithBuilding, {});
      rb.query('associatedWithMortgage', params.associatedWithMortgage, {});
      rb.query('associatedWithApplication', params.associatedWithApplication, {});
      rb.query('associatedWithCustomer', params.associatedWithCustomer, {});
      rb.query('documentTypeId', params.documentTypeId, {});
      rb.query('hasDocumentType', params.hasDocumentType, {});
      rb.query('uploadMonth', params.uploadMonth, {});
      rb.query('uploadYear', params.uploadYear, {});
      rb.query('buildingId', params.buildingId, {});
      rb.query('applicationId', params.applicationId, {});
      rb.query('mortgageId', params.mortgageId, {});
      rb.query('taskId', params.taskId, {});
      rb.query('isUserUploaded', params.isUserUploaded, {});
      rb.query('needsValidation', params.needsValidation, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Documents>;
      })
    );
  }

  /**
   * Gets the documents for the current organization.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getDocuments$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDocuments(params?: {

    /**
     * Mandatory: the customerId for which to retrieve the documents
     */
    customerId?: string;

    /**
     * The number of documents to be retrieved
     */
    count?: number;

    /**
     * The page that's used to offset the documents based on the count
     */
    page?: number;

    /**
     * Only retrieve documents of type that associated with a building
     */
    associatedWithBuilding?: boolean;

    /**
     * Only retrieve documents of type that associated with a mortgage
     */
    associatedWithMortgage?: boolean;

    /**
     * Only retrieve documents of type that associated with an application
     */
    associatedWithApplication?: boolean;

    /**
     * Only retrieve documents associated with a customer
     */
    associatedWithCustomer?: boolean;

    /**
     * Only retrieve documents of a particular documentType
     */
    documentTypeId?: string;

    /**
     * Only retrieve documents that have a document type or not (leave empty to get all)
     */
    hasDocumentType?: boolean;

    /**
     * Only retrieve documents uploaded in this month
     */
    uploadMonth?: number;

    /**
     * Only retrieve documents uploaded in this year
     */
    uploadYear?: number;

    /**
     * Only retrieve documents associated with this building
     */
    buildingId?: string;

    /**
     * Only retrieve documents associated with this application
     */
    applicationId?: string;

    /**
     * Only retrieve documents associated with this mortgage
     */
    mortgageId?: string;

    /**
     * Only retrieve documents associated with this task
     */
    taskId?: string;

    /**
     * Only retrieve documents that are user uploaded or non-user uploaded
     */
    isUserUploaded?: boolean;

    /**
     * Only retrieve documents that were classified automatically but not yet validated by a user
     */
    needsValidation?: boolean;
  },
  context?: HttpContext

): Observable<Documents> {

    return this.getDocuments$Response(params,context).pipe(
      map((r: StrictHttpResponse<Documents>) => r.body as Documents)
    );
  }

  /**
   * Path part for operation registerDocument
   */
  static readonly RegisterDocumentPath = '/api/v1/Documents';

  /**
   * Registers a document that has been uploaded to the document storage into the organizations document collection.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `registerDocument$Plain()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  registerDocument$Plain$Response(params?: {

    /**
     * The document details to be registered
     */
    body?: RegisterDocument
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Document>> {

    const rb = new RequestBuilder(this.rootUrl, DocumentsService.RegisterDocumentPath, 'post');
    if (params) {
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Document>;
      })
    );
  }

  /**
   * Registers a document that has been uploaded to the document storage into the organizations document collection.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `registerDocument$Plain$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  registerDocument$Plain(params?: {

    /**
     * The document details to be registered
     */
    body?: RegisterDocument
  },
  context?: HttpContext

): Observable<Document> {

    return this.registerDocument$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<Document>) => r.body as Document)
    );
  }

  /**
   * Registers a document that has been uploaded to the document storage into the organizations document collection.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `registerDocument()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  registerDocument$Response(params?: {

    /**
     * The document details to be registered
     */
    body?: RegisterDocument
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Document>> {

    const rb = new RequestBuilder(this.rootUrl, DocumentsService.RegisterDocumentPath, 'post');
    if (params) {
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Document>;
      })
    );
  }

  /**
   * Registers a document that has been uploaded to the document storage into the organizations document collection.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `registerDocument$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  registerDocument(params?: {

    /**
     * The document details to be registered
     */
    body?: RegisterDocument
  },
  context?: HttpContext

): Observable<Document> {

    return this.registerDocument$Response(params,context).pipe(
      map((r: StrictHttpResponse<Document>) => r.body as Document)
    );
  }

  /**
   * Path part for operation deleteDocument
   */
  static readonly DeleteDocumentPath = '/api/v1/Documents';

  /**
   * Deletes a document from the organizations collection.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteDocument()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteDocument$Response(params?: {

    /**
     * the id of the document
     */
    id?: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, DocumentsService.DeleteDocumentPath, 'delete');
    if (params) {
      rb.query('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Deletes a document from the organizations collection.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteDocument$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteDocument(params?: {

    /**
     * the id of the document
     */
    id?: string;
  },
  context?: HttpContext

): Observable<void> {

    return this.deleteDocument$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation uploadDocument
   */
  static readonly UploadDocumentPath = '/api/v1/Documents/upload';

  /**
   * Streams a file directly into blob storage. Follow up with POST /documents to register the document.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `uploadDocument$Plain()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadDocument$Plain$Response(params?: {
    body?: {
'file'?: Blob;
}
  },
  context?: HttpContext

): Observable<StrictHttpResponse<UploadedDocumentResult>> {

    const rb = new RequestBuilder(this.rootUrl, DocumentsService.UploadDocumentPath, 'post');
    if (params) {
      rb.body(params.body, 'multipart/form-data');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<UploadedDocumentResult>;
      })
    );
  }

  /**
   * Streams a file directly into blob storage. Follow up with POST /documents to register the document.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `uploadDocument$Plain$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadDocument$Plain(params?: {
    body?: {
'file'?: Blob;
}
  },
  context?: HttpContext

): Observable<UploadedDocumentResult> {

    return this.uploadDocument$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<UploadedDocumentResult>) => r.body as UploadedDocumentResult)
    );
  }

  /**
   * Streams a file directly into blob storage. Follow up with POST /documents to register the document.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `uploadDocument()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadDocument$Response(params?: {
    body?: {
'file'?: Blob;
}
  },
  context?: HttpContext

): Observable<StrictHttpResponse<UploadedDocumentResult>> {

    const rb = new RequestBuilder(this.rootUrl, DocumentsService.UploadDocumentPath, 'post');
    if (params) {
      rb.body(params.body, 'multipart/form-data');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<UploadedDocumentResult>;
      })
    );
  }

  /**
   * Streams a file directly into blob storage. Follow up with POST /documents to register the document.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `uploadDocument$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadDocument(params?: {
    body?: {
'file'?: Blob;
}
  },
  context?: HttpContext

): Observable<UploadedDocumentResult> {

    return this.uploadDocument$Response(params,context).pipe(
      map((r: StrictHttpResponse<UploadedDocumentResult>) => r.body as UploadedDocumentResult)
    );
  }

  /**
   * Path part for operation downloadDocument
   */
  static readonly DownloadDocumentPath = '/api/v1/Documents/{id}/content';

  /**
   * Streams a document directly from blob storage to the client (proxy download — no SAS URL).
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `downloadDocument()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadDocument$Response(params: {
    id: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, DocumentsService.DownloadDocumentPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Streams a document directly from blob storage to the client (proxy download — no SAS URL).
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `downloadDocument$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadDocument(params: {
    id: string;
  },
  context?: HttpContext

): Observable<void> {

    return this.downloadDocument$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation zipDocuments
   */
  static readonly ZipDocumentsPath = '/api/v1/Documents/zip';

  /**
   * Streams multiple documents as a zip directly to the client.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `zipDocuments()` instead.
   *
   * This method doesn't expect any request body.
   */
  zipDocuments$Response(params?: {
    documentIds?: Array<string>;
    downloadFileName?: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, DocumentsService.ZipDocumentsPath, 'get');
    if (params) {
      rb.query('documentIds', params.documentIds, {});
      rb.query('downloadFileName', params.downloadFileName, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Streams multiple documents as a zip directly to the client.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `zipDocuments$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  zipDocuments(params?: {
    documentIds?: Array<string>;
    downloadFileName?: string;
  },
  context?: HttpContext

): Observable<void> {

    return this.zipDocuments$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getDocumentTypes
   */
  static readonly GetDocumentTypesPath = '/api/v1/DocumentTypes';

  /**
   * Returns a list of available document types.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getDocumentTypes$Plain()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDocumentTypes$Plain$Response(params?: {
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<DocumentType>>> {

    const rb = new RequestBuilder(this.rootUrl, DocumentsService.GetDocumentTypesPath, 'get');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<DocumentType>>;
      })
    );
  }

  /**
   * Returns a list of available document types.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getDocumentTypes$Plain$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDocumentTypes$Plain(params?: {
  },
  context?: HttpContext

): Observable<Array<DocumentType>> {

    return this.getDocumentTypes$Plain$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<DocumentType>>) => r.body as Array<DocumentType>)
    );
  }

  /**
   * Returns a list of available document types.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getDocumentTypes()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDocumentTypes$Response(params?: {
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<DocumentType>>> {

    const rb = new RequestBuilder(this.rootUrl, DocumentsService.GetDocumentTypesPath, 'get');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<DocumentType>>;
      })
    );
  }

  /**
   * Returns a list of available document types.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getDocumentTypes$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDocumentTypes(params?: {
  },
  context?: HttpContext

): Observable<Array<DocumentType>> {

    return this.getDocumentTypes$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<DocumentType>>) => r.body as Array<DocumentType>)
    );
  }

  /**
   * Path part for operation getUsedDocumentTypeIds
   */
  static readonly GetUsedDocumentTypeIdsPath = '/api/v1/DocumentTypes/Used';

  /**
   * Returns the ids of the document types that are in use (by a document, a required document or a form) and can't be deleted (admins only).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getUsedDocumentTypeIds()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUsedDocumentTypeIds$Response(params?: {
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<string>>> {

    const rb = new RequestBuilder(this.rootUrl, DocumentsService.GetUsedDocumentTypeIdsPath, 'get');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<string>>;
      })
    );
  }

  /**
   * Returns the ids of the document types that are in use (by a document, a required document or a form) and can't be deleted (admins only).
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getUsedDocumentTypeIds$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUsedDocumentTypeIds(params?: {
  },
  context?: HttpContext

): Observable<Array<string>> {

    return this.getUsedDocumentTypeIds$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<string>>) => r.body as Array<string>)
    );
  }

  /**
   * Path part for operation addDocumentType
   */
  static readonly AddDocumentTypePath = '/api/v1/DocumentTypes';

  /**
   * Adds a document type, shared by every organization (admins only).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `addDocumentType()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  addDocumentType$Response(params?: {
    body?: DocumentType
  },
  context?: HttpContext

): Observable<StrictHttpResponse<DocumentType>> {

    const rb = new RequestBuilder(this.rootUrl, DocumentsService.AddDocumentTypePath, 'post');
    if (params) {
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<DocumentType>;
      })
    );
  }

  /**
   * Adds a document type, shared by every organization (admins only).
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `addDocumentType$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  addDocumentType(params?: {
    body?: DocumentType
  },
  context?: HttpContext

): Observable<DocumentType> {

    return this.addDocumentType$Response(params,context).pipe(
      map((r: StrictHttpResponse<DocumentType>) => r.body as DocumentType)
    );
  }

  /**
   * Path part for operation updateDocumentType
   */
  static readonly UpdateDocumentTypePath = '/api/v1/DocumentTypes/{id}';

  /**
   * Changes the settings of a document type; the id can't change (admins only).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateDocumentType()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateDocumentType$Response(params: {
    id: string;
    body?: DocumentType
  },
  context?: HttpContext

): Observable<StrictHttpResponse<DocumentType>> {

    const rb = new RequestBuilder(this.rootUrl, DocumentsService.UpdateDocumentTypePath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<DocumentType>;
      })
    );
  }

  /**
   * Changes the settings of a document type; the id can't change (admins only).
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateDocumentType$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateDocumentType(params: {
    id: string;
    body?: DocumentType
  },
  context?: HttpContext

): Observable<DocumentType> {

    return this.updateDocumentType$Response(params,context).pipe(
      map((r: StrictHttpResponse<DocumentType>) => r.body as DocumentType)
    );
  }

  /**
   * Path part for operation deleteDocumentType
   */
  static readonly DeleteDocumentTypePath = '/api/v1/DocumentTypes/{id}';

  /**
   * Deletes a document type that is not in use (admins only).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteDocumentType()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteDocumentType$Response(params: {
    id: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, DocumentsService.DeleteDocumentTypePath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Deletes a document type that is not in use (admins only).
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteDocumentType$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteDocumentType(params: {
    id: string;
  },
  context?: HttpContext

): Observable<void> {

    return this.deleteDocumentType$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getRequiredDocumentRules
   */
  static readonly GetRequiredDocumentRulesPath = '/api/v1/RequiredDocumentRules';

  /**
   * Returns every required document rule (super admins only).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRequiredDocumentRules()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRequiredDocumentRules$Response(params?: {
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<RequiredDocumentRule>>> {

    const rb = new RequestBuilder(this.rootUrl, DocumentsService.GetRequiredDocumentRulesPath, 'get');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<RequiredDocumentRule>>;
      })
    );
  }

  /**
   * Returns every required document rule (super admins only).
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getRequiredDocumentRules$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRequiredDocumentRules(params?: {
  },
  context?: HttpContext

): Observable<Array<RequiredDocumentRule>> {

    return this.getRequiredDocumentRules$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<RequiredDocumentRule>>) => r.body as Array<RequiredDocumentRule>)
    );
  }

  /**
   * Path part for operation getRequiredDocumentConditions
   */
  static readonly GetRequiredDocumentConditionsPath = '/api/v1/RequiredDocumentRules/Conditions';

  /**
   * Returns the conditions a required document rule can combine (super admins only).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRequiredDocumentConditions()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRequiredDocumentConditions$Response(params?: {
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<RequiredDocumentCondition>>> {

    const rb = new RequestBuilder(this.rootUrl, DocumentsService.GetRequiredDocumentConditionsPath, 'get');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<RequiredDocumentCondition>>;
      })
    );
  }

  /**
   * Returns the conditions a required document rule can combine (super admins only).
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getRequiredDocumentConditions$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRequiredDocumentConditions(params?: {
  },
  context?: HttpContext

): Observable<Array<RequiredDocumentCondition>> {

    return this.getRequiredDocumentConditions$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<RequiredDocumentCondition>>) => r.body as Array<RequiredDocumentCondition>)
    );
  }

  /**
   * Path part for operation addRequiredDocumentRule
   */
  static readonly AddRequiredDocumentRulePath = '/api/v1/RequiredDocumentRules';

  /**
   * Adds a required document rule; applications that already exist keep their required documents (super admins only).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `addRequiredDocumentRule()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  addRequiredDocumentRule$Response(params?: {
    body?: RequiredDocumentRule
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RequiredDocumentRule>> {

    const rb = new RequestBuilder(this.rootUrl, DocumentsService.AddRequiredDocumentRulePath, 'post');
    if (params) {
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RequiredDocumentRule>;
      })
    );
  }

  /**
   * Adds a required document rule; applications that already exist keep their required documents (super admins only).
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `addRequiredDocumentRule$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  addRequiredDocumentRule(params?: {
    body?: RequiredDocumentRule
  },
  context?: HttpContext

): Observable<RequiredDocumentRule> {

    return this.addRequiredDocumentRule$Response(params,context).pipe(
      map((r: StrictHttpResponse<RequiredDocumentRule>) => r.body as RequiredDocumentRule)
    );
  }

  /**
   * Path part for operation updateRequiredDocumentRule
   */
  static readonly UpdateRequiredDocumentRulePath = '/api/v1/RequiredDocumentRules/{id}';

  /**
   * Changes a required document rule; applications that already exist keep their required documents (super admins only).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateRequiredDocumentRule()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateRequiredDocumentRule$Response(params: {
    id: string;
    body?: RequiredDocumentRule
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RequiredDocumentRule>> {

    const rb = new RequestBuilder(this.rootUrl, DocumentsService.UpdateRequiredDocumentRulePath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RequiredDocumentRule>;
      })
    );
  }

  /**
   * Changes a required document rule; applications that already exist keep their required documents (super admins only).
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateRequiredDocumentRule$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  updateRequiredDocumentRule(params: {
    id: string;
    body?: RequiredDocumentRule
  },
  context?: HttpContext

): Observable<RequiredDocumentRule> {

    return this.updateRequiredDocumentRule$Response(params,context).pipe(
      map((r: StrictHttpResponse<RequiredDocumentRule>) => r.body as RequiredDocumentRule)
    );
  }

  /**
   * Path part for operation deleteRequiredDocumentRule
   */
  static readonly DeleteRequiredDocumentRulePath = '/api/v1/RequiredDocumentRules/{id}';

  /**
   * Deletes a required document rule; applications that already exist keep their required documents (super admins only).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteRequiredDocumentRule()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteRequiredDocumentRule$Response(params: {
    id: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, DocumentsService.DeleteRequiredDocumentRulePath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Deletes a required document rule; applications that already exist keep their required documents (super admins only).
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteRequiredDocumentRule$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteRequiredDocumentRule(params: {
    id: string;
  },
  context?: HttpContext

): Observable<void> {

    return this.deleteRequiredDocumentRule$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

}
